import { Injectable } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { DataCacheService } from './data-cache.service';
import { ApiPatientService } from './api-call-providers/api-patient.service';
import { ApiPatientVitalgrafService } from './api-call-providers/api-patient-vitalgraf.service';
import { COOKIES_CONFIG } from './../configs/cookies.config';
import * as moment from 'moment';

export class FILTER_KANA_CONFIG {
  static readonly FILTER = {
    "あ": [
      /^[\u3041-\u304A]/g, // - あ行
      /^[\u30A1-\u30AA]/g, // - ア行
      /^[\uFF67-\uFF75]/g  // - ｱ行
    ],
    "か": [
      /^[\u304B-\u3054]/g, // - か行
      /^[\u30AB-\u30B2]/g, // - カ行
      /^[\uFF76-\uFF7A]/g, // - ｶ行
    ],
    "さ": [
      /^[\u3055-\u305E]/g,  // - さ行
      /^[\u30B5-\u30BD]/g,  // - サ行
      /^[\uFF7B-\uFF7F]/g,  // - ｻ行
    ],
    "た": [
      /^[\u305F-\u3069]/g,  // - た行
      /^[\u30BF-\u30C9]/g,  // - タ行
      /^[\uFF80-\uFF84]/g,  // - ﾀ行
    ],
    "な": [
      /^[\u306A-\u306E]/g,  // - な行
      /^[\u30CA-\u30CE]/g,  // - ナ行
      /^[\uFF85-\uFF89]/g,  // - ﾅ行
    ],
    "は": [
      /^[\u306F-\u307D]/g,  // - は行
      /^[\u30CF-\u30DD]/g,  // - ハ行
      /^[\uFF8A-\uFF8E]/g,  // - ﾊ行
    ],
    "ま": [
      /^[\u307E-\u3082]/g,  // - ま行
      /^[\u30DE-\u30E2]/g,  // - マ行
      /^[\uFF8F-\uFF93]/g,  // - ﾏ行
    ],
    "や": [
      /^[\u3083-\u3088]/g,  // - や行
      /^[\u30E3-\u30E8]/g,  // - ヤ行
      /^[\uFF6C-\uFF6E]/g,  // - ﾔ行
      /^[\uFF94-\uFF96]/g,  // - ﾔ行
    ],
    "ら": [
      /^[\u3089-\u308D]/g,  // - ら行
      /^[\u30E9-\u30ED]/g,  // - ラ行
      /^[\uFF97-\uFF9B]/g,  // - ﾗ行
    ],
    "わ": [
      /^[\u308f-\u3093]/g,  // - わ行
      /^[\u30EE-\u30F3]/g,  // - ワ行
      /^[\uFF9C-\uFF9D]/g,  // - ﾜ行
    ]
  };
}


export class Patient {

  // TODO: 利用しない予定
  patientName: string = '';

  icon: string = '';

  patientId: string = '';
  patientCode: string = '';
  name: string = '';
  nameKana: string = '';
  gender: string = '';
  birthday: string = '';
  wardName: string = '';
  unitName: string = '';
  roomName: string = '';
  bedNo: string = '';
  memo: string = '';
  patientIcon?: string = '';

  sleepAt?: number;
  wakeupAt?: number;

  constructor(data) {
    this.patientId = data.patientId;
    this.patientCode = data.patientCode;
    this.name = data.name;
    this.nameKana = data.nameKana;
    this.gender = data.gender;
    this.birthday = data.birthday;

    this.wardName = data.wardName;
    this.unitName = data.unitName;
    this.roomName = data.roomName;
    this.bedNo = data.bedNo;
    this.memo = data.memo;

    // TODO: 利用しない予定
    this.patientName = data.patientName;
    this.icon = data.icon;
    this.patientIcon = data.patientIcon;

    this.sleepAt = data.sleepAt;
    this.wakeupAt = data.wakeupAt;

  }
}

@Injectable()
export class PatientService {

  // 患者リスト
  private patientsSource = new Subject<any>();
  patients = this.patientsSource.asObservable();

  // バイタル付き患者リスト
  private patientsWithVitalsSource = new Subject<Patient[]>();
  patientsWithVitals = this.patientsWithVitalsSource.asObservable();

  // 睡眠センサーに紐づく患者リスト
  private patientsWithSleepSensorSource = new Subject<any>();
  patientsWithSleepSensor$ = this.patientsWithSleepSensorSource.asObservable();

  // 患者登録
  private registPatientSource = new Subject<Patient[]>();
  registPatient = this.registPatientSource.asObservable();

  // 患者一括登録
  private bulkRegistPatientSource = new Subject<any>();
  bulkRegistPatient = this.bulkRegistPatientSource.asObservable();

  // 患者更新
  private updatePatientSource = new Subject<Patient[]>();
  updatePatient = this.updatePatientSource.asObservable();

  // 複数画面で絞り込み条件を使い回したいとき
  selectedTargetDate: string = '';
  selectedKana: string = 'all';
  selectedWard: string = 'all';
  selectedUnit: string = 'all';
  selectedRoom: string = 'all';
  onlyHasVitalLog: string = 'all';

  // 検索条件でのキャッシュ
  queryCacheTable: {
    [queryKey: string]: {
      data: any;
      time: number;
    }
  } = {};

  constructor(
    private apiPatientService: ApiPatientService,
    private apiPatientVitalgrafService: ApiPatientVitalgrafService,
    private _dataCacheService: DataCacheService,
  ) {
  }

  get loading() {
    return this.apiPatientService.isLoading
  }

  get loadingGraph() {
    return this.apiPatientVitalgrafService.isLoading
  }

  /**
   * 患者データの初期化
   */
  async load() {
    this.apiPatientService.isLoading = true
    let _data = await this._dataCacheService.get(COOKIES_CONFIG.PATIENTS_DATA)
    if (!_data.isExist()) {
      // キャッシュデータが古い or ない場合、データロード
      await this.fetch();
      return
    }
    this.apiPatientService.isLoading = false
    return this.patientsSource.next(_data);

  }

  /**
   * APIを叩いて、患者情報を取得する
   * @param params
   */
  async fetch() {
    let res: any = await this.apiPatientService.list()
    let data = { data: res.patientList }
    if (res.expired) {
      data = await this._dataCacheService.set(COOKIES_CONFIG.PATIENTS_DATA, res.patientList, res.expired)
    }
    this.patientsSource.next(data)
  }

  /**
   * 利用者のバイタルのグラフ情報を取得する
   */
  async fetchVitalGraf(patientId: string, params?: any) {
    let res: any = await this.apiPatientVitalgrafService.get(patientId, params)
    this.patientsWithVitalsSource.next(res)
  }

  /**
   * 指定のtypeのバイタルを、期間指定で取得するAPI
   */
  async getVitalPeriod(_id: string, start: number, end: number, types: string[]): Promise<any> {
    return this.apiPatientVitalgrafService
      .getByTypesBetween({
        patientId: _id,
        start: start,
        end: end,
        types: types,
      })
      .then((res) => {
        return res ? res.data || {} : {};
      })
      .catch(() => { });
  }


  /**
   * 利用者情報登録
   * @param params
   */
  async regist(params) {
    await this.apiPatientService.post(params)
    // データを再読込
    await this.fetch()
    this.registPatientSource.next()
  }

  /**
   * 利用者情報一括登録
   * @param params
   */
  async bulkRegist(params) {
    await this.apiPatientService.bulkPost(params);
    // データを再読込
    await this.fetch();
    this.bulkRegistPatientSource.next();
  }

  /**
   * 利用者情報更新
   * @param params
   */
  async update(params, message = null) {
    await this.apiPatientService.update(params, message)
    // データを再読込
    await this.fetch()
    this.updatePatientSource.next()
  }

  /**
   * 利用者情報物理削除
   * @param params
   */
  async delete(params) {
    await this.apiPatientService.delete(params)
    await this.fetch()
  }

  /**
   * 利用者情報論理削除
   * @param params
   */
  async softDelete(params) {
    await this.apiPatientService.softDelete(params)
    await this.fetch()
  }

  /**
   * ユニット&ルーム情報をリスト化する
   * @param patients
   * @param unitGroyp
   * @param roomGroyp
   * @param unitList
   * @param roomList
   */
  async listUnitAndRoom(patients) {

    let wardGroyp = {};
    let unitGroyp = {};
    let roomGroyp = {};

    if (!patients) {
      return Promise.resolve({
        wardGroyp: wardGroyp,
        unitGroyp: unitGroyp,
        roomGroyp: roomGroyp,
        wardList: [],
        unitList: [],
        roomList: [],
      })
    }

    patients.forEach((_patient: any) => {
      if (_patient.wardName) {
        wardGroyp[_patient.wardName] = { wardName: _patient.wardName, isShow: true };
      }
      if (_patient.unitName) {
        unitGroyp[_patient.unitName] = { unitName: _patient.unitName, isShow: true };
      }
      if (_patient.roomName) {
        roomGroyp[_patient.roomName] = { roomName: _patient.roomName, isShow: true };
      }
    });
    let wardList = Object.keys(wardGroyp).map((wardKey) => { return wardGroyp[wardKey]; }).sort((a, b) => a.wardName > b.wardName ? 1 : -1);
    let unitList = Object.keys(unitGroyp).map((unitKey) => { return unitGroyp[unitKey]; }).sort((a, b) => a.unitName > b.unitName ? 1 : -1);
    let roomList = Object.keys(roomGroyp).map((roomKey) => { return roomGroyp[roomKey]; }).sort((a, b) => a.roomName > b.roomName ? 1 : -1);

    return Promise.resolve({
      wardGroyp: wardGroyp,
      unitGroyp: unitGroyp,
      roomGroyp: roomGroyp,
      wardList: wardList,
      unitList: unitList,
      roomList: roomList,
    })
  }

  async filterList(patientList, selectedWard, selectedUnit, selectedRoom, selectedKana = 'all', targetPatientIdList = null) {
    // - フロアーの処理
    if (selectedWard !== 'all') {
      patientList = patientList.filter(_data => {
        return (_data.wardName === selectedWard);
      });
    }
    // - グループの処理
    if (selectedUnit !== 'all') {
      patientList = patientList.filter(_data => {
        return (_data.unitName === selectedUnit);
      });
    }
    // - 部屋の処理
    if (selectedRoom !== 'all') {
      patientList = patientList.filter(_data => {
        return (_data.roomName === selectedRoom);
      });
    }

    // - あいうえお絞り込み
    if (selectedKana !== 'all') {
      patientList = this.filterByKana(patientList, selectedKana);
    }

    // - 特定の利用者IDで絞り込む
    if (targetPatientIdList && Array.isArray(targetPatientIdList)) {
      let indexIds: any = {};
      targetPatientIdList.forEach(p => indexIds[p.patientId] = true);
      patientList = patientList.filter(_data => {
        return (indexIds[_data.patientId]) ? true : false;
      });
    }

    return Promise.resolve(patientList)
  }

  async checkSelect(patientList, selectPatientId) {
    let patient = patientList.find(_p => {
      return _p.patientId === selectPatientId;
    });
    if (!patient) {
      alert('利用者が選択されていません');
      return;
    }
    return Promise.resolve(patient)
  }


  filterByKana(patientList, kana) {
    let list = FILTER_KANA_CONFIG.FILTER[kana];
    if (list) {
      patientList = patientList.filter(_data => {
        return list.find(_strMatch => {
          if (_data.nameKana.match(_strMatch)) {
            return true;
          }
          return false;
        });
      });
    } else {
      // confがない場合は、上記式に当てはまらないデータを抽出することになる
      let filterList = [];
      Object.keys(FILTER_KANA_CONFIG.FILTER).forEach(key => {
        filterList = filterList.concat(FILTER_KANA_CONFIG.FILTER[key]);
      });
      patientList = patientList.filter(_data => {
        let result = filterList.find(_strMatch => {
          if (_data.nameKana.match(_strMatch)) {
            return true;
          }
          return false;
        });
        return !result;
      });
    }
    return patientList;
  }


  // 利用者検索（主に利用予定の有無で使っている）
  async searchPatient(query: any = {}, showLoading: boolean = false) {
    // // 検索条件オブジェクトからキャッシュ用文字列を生成する
    // const cacheKey = this._makeQueryCacheKey(query);
    // // 5分間は、キャッシュを保持する
    // const timestamp = moment().unix();
    // if (this.queryCacheTable.hasOwnProperty(cacheKey) && this.queryCacheTable[cacheKey].time + 300 > timestamp) {
    //    return this.queryCacheTable[cacheKey].data;
    //  }
    let data = await this.apiPatientService.search(query, showLoading);
    return data;
    // this.queryCacheTable[cacheKey] = {
    //    time: timestamp,
    //    data: (data) ? data.patientList : [],
    //  }
    //  return this.queryCacheTable[cacheKey].data;
  }

  clearQueryCache() {
    this.queryCacheTable = {};
  }

  /**
   * 睡眠センサーに紐づく患者データの初期化
   */
  async loadWithSleepSensor(targetDate: string = null) {
    const result = await this.apiPatientService.listWithSleepSensor(targetDate);
    this.patientsWithSleepSensorSource.next(result);
  }

  /**
   * Promiseで返す
   */
  async getSleepLogs(sensorId: string, targetDate: string = null) {
    return this.apiPatientService.getSleepLogs(sensorId, targetDate);
  }


  // アルファベット順にキーをソートしたjson文字列を作成する
  _makeQueryCacheKey(query: object) {
    // https://stackoverflow.com/questions/16167581/sort-object-properties-and-json-stringify
    const flattenQuery = this._flattenObj(query);
    return JSON.stringify(flattenQuery, Object.keys(flattenQuery).sort());
  }
  // オブジェクトをflattenする
  _flattenObj(obj) {
    const toReturn = {};
    for (const i in obj) {
      if (!obj.hasOwnProperty(i)) continue;
      // 再帰
      if ((typeof obj[i]) == 'object') {
        const flatObject = this._flattenObj(obj[i]);
        for (const x in flatObject) {
          if (!flatObject.hasOwnProperty(x)) continue;
          toReturn[i + '.' + x] = flatObject[x];
        }
      } else {
        toReturn[i] = obj[i];
      }
    }
    return toReturn;
  }

  /**
   * 印刷用のバイタルデータを取得する
   */
  async getVitalOutputCSV(patientId: string, start: number, end: number) {
    return this.apiPatientService.getVitalOutputCSV(patientId, start, end);
  }

  // バイタリーの曜日やAM/PM指定の処理
  getDaysOfTheWeekKey(): string {
    let daysOfTheWeek = "";
    console.log(moment().day());
    switch (moment().day()) {
      case 0:
        daysOfTheWeek = 'sun';
        break;
      case 1:
        daysOfTheWeek = 'mon';
        break;
      case 2:
        daysOfTheWeek = 'tue';
        break;
      case 3:
        daysOfTheWeek = 'wed';
        break;
      case 4:
        daysOfTheWeek = 'thu';
        break;
      case 5:
        daysOfTheWeek = 'fri';
        break;
      case 6:
        daysOfTheWeek = 'sat';
        break;
    }
    return daysOfTheWeek;
  }
  // selectedTargetDate: 'today' | 'todayAM' | 'todayPM'
  isDayServiceSchedule(selectedTargetDate: string, dayServiceSchedule: {
    sun: { am: boolean, pm: boolean, comment: string},
    mon: { am: boolean, pm: boolean, comment: string },
    tue: { am: boolean, pm: boolean, comment: string },
    wed: { am: boolean, pm: boolean, comment: string },
    thu: { am: boolean, pm: boolean, comment: string },
    fri: { am: boolean, pm: boolean, comment: string },
    sat: { am: boolean, pm: boolean, comment: string },
  } | null): boolean {
    if (!dayServiceSchedule) return false;

    const daysOfTheWeek = this.getDaysOfTheWeekKey();
    // 今日の曜日を取得する
    const todayInfo: { am: boolean, pm: boolean } | null = dayServiceSchedule[daysOfTheWeek];
    if (!todayInfo) return false;

    // 当日指定
    if (selectedTargetDate === "today") {
      return (todayInfo.am || todayInfo.pm);
    }
    // AM指定
    if (selectedTargetDate === "todayAM") {
      return todayInfo.am;
    }
    // PM指定
    if (selectedTargetDate === "todayPM") {
      return todayInfo.pm;
    }
    return false;
  }

  // バイタル計測があるかどうかをチェックする
  // dashboard API のレスポンスの一部がvitalsとして渡ってくる
  isVitalMeasured(onlyHasVitalLog: string, vitals) {
    if (onlyHasVitalLog === "measured") {
      // 計測がある人を表示する
      if (!vitals) return false;
      // 1件でもバイタル記録があれば
      return Object.keys(vitals).some((type) => vitals[type].length > 0);
    } else if (onlyHasVitalLog === "notMeasured") {
      // 計測が全くない人を表示
      if (!vitals) return true;
      return Object.keys(vitals).every((type) => vitals[type].length === 0);
    } else if (onlyHasVitalLog === "notBTMeasured") {
      // 体温の計測が全くない人を表示
      if (!vitals) return true;
      return !vitals['BT'] || vitals['BT'].length === 0;
    } else if (onlyHasVitalLog === "notBPMeasured") {
      // 血圧の計測が全くない人を表示
      if (!vitals) return true;
      return !vitals['BP'] || vitals['BP'].length === 0;
    } else if (onlyHasVitalLog === "notSpO2Measured") {
      // SpO2の計測が全くない人を表示
      if (!vitals) return true;
      return !vitals['SpO2'] || vitals['SpO2'].length === 0;
    } else if (onlyHasVitalLog === "measuredBT370") {
      // 体温37度超えの方
      if (!vitals) return false;
      return vitals['BT'] && vitals['BT'].some((data) => Number(data.value) >= 37);
    } else if (onlyHasVitalLog === "measuredBPH140") {
      // 血圧140超えの方
      if (!vitals) return false;
      return vitals['BP'] && vitals['BP'].some((data) => data["BPH"] && Number(data["BPH"].value) >= 140);
    }
    return false;

  }

}



// WEBPACK FOOTER //
// ./src/providers/patient.service.ts